import React, { useRef, useState, useEffect } from "react";
import { useQuery } from "react-query";
import toast, { Toaster } from "react-hot-toast";
import { DELETE, GET, POST } from "../../api/requests";
import { useVirtualizer } from "@tanstack/react-virtual";
import AutomationItem from "./AutomationItem";
import { FolderUp, Search } from "lucide-react";
import Dropzone from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import AllDone from "../../components/EmptyStates/AllDone";
import ApprovalItem from "./ApprovalItem";
import { ColorRing } from "react-loader-spinner";
import Fuse from 'fuse.js';
import _ from "lodash";

export default function Automations() {
  const parentRef = useRef();
  const [selectedTab, setSelectedTab] = useState("Review");
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const {
    data: automations,
    isLoading,
    refetch,
  } = useQuery(
    ["automations", selectedTab],
    async () => {
      if (selectedTab === "Review") {
        const response = await GET("/approvals");
        return response;
      } else {
        const response = await GET("/approvals?completed=true");
        return response;
      }
    },
    {
      refetchInterval: 30000, // Refetch every 5 seconds
    }
  );

  useEffect(() => {
    refetch();
  }, [selectedTab, refetch]);

  const buildAutomation = async (file) => {
    console.log("Building");
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      // Add schema to the formData if needed
      // formData.append("schema", JSON.stringify(yourSchemaObject));

      const response = await POST("/documents/deliveryOrder", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("response", response);
      if (response.status === 200) {
        const { message, data, fileUrl, fileKey } = response.data;
        console.log("Document processed:", message);
        console.log("File URL:", fileUrl);
        console.log("File Key:", fileKey);

        toast("File processed successfully");
        setDroppedFiles((prevFiles) => [...prevFiles, file.name]);
        setCurrentFileIndex(
          (prevIndex) => (prevIndex + 1) % (droppedFiles.length + 1)
        );

        // You can do more with the processed data here if needed
        // For example, update state with the processed data
        // setProcessedData(data);
      }
    } catch (error) {
      console.error("Error processing file:", error);
      toast.error("Failed to process file");
    } finally {
      setIsUploading(false);
    }
  };

  // Configure Fuse search options
  const fuseOptions = {
    keys: [
      'descopedContextObject.secondaryReferenceNo',
      'descopedContextObject.data.hbl',
      'descopedContextObject.data.mbl',
      "descopedContextObject.data.consignee_name" 
    ],
    threshold: 0.3
  };

  // Create fuse instance and filter automations based on search
  const fuse = automations ? new Fuse(automations, fuseOptions) : null;
  const filteredAutomations = searchQuery && fuse 
    ? fuse.search(searchQuery).map(result => result.item)
    : automations;

  // Sort automations based on orderByPath if it exists
  const sortedAutomations = filteredAutomations?.sort((a, b) => {
    const aOrderByPath = a.currentNode?.orderByPath;
    const bOrderByPath = b.currentNode?.orderByPath;

    if (aOrderByPath && bOrderByPath) {
      const aValue = _.get(a.contextData, aOrderByPath);
      const bValue = _.get(b.contextData, bOrderByPath);
      return aValue
        ? bValue
          ? aValue.localeCompare(bValue)
          : 1
        : bValue
        ? -1
        : 0;
    }
    return 0;
  });

  // Handle deletion of automation items
  const handleDelete = async (id) => {
    try {
      await DELETE(`/approvals/${id}`);

      refetch(); // Refetch the automations list after deletion
      toast.success('Automation deleted successfully');
    } catch (error) {
      console.error('Error deleting automation:', error);
      toast.error('Failed to delete automation');
    }
  };

  // virtualize the list of workflows
  const rowVirtualizer = useVirtualizer({
    count: sortedAutomations?.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 300,
    overscan: 5,
  });

  const tabs = [
    { name: "Review", current: true },
    { name: "All automations", current: false },
  ];

  const fileVariants = {
    initial: { y: 50, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -50, opacity: 0 },
  };

  const spring = {
    type: "spring",
    stiffness: 700,
    damping: 30,
  };

  return (
    <div className="w-full">
      <div
        style={{ zIndex: 10, backdropFilter: "blur(9px)" }}
        className="sticky bg-white h-14 flex items-center justify-between w-full top-0 border-slate-200 bg-opacity-60 border-b pl-6 pr-4"
      >
        <div className="flex items-center space-x-4 flex-1">
          <h1 className="text-sm font-semibold text-slate-800">
            Available automations
          </h1>

          <div className="relative">
            <input 
              type="text" 
              style={{width: 160, height:32}} 
              placeholder="Search shipments..." 
              className="rounded-lg text-xs bg-transparent mr-2 border-0 pl-7 focus:outline-none focus:ring-0"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
          </div>
        </div>

        {true == false && (
          <div className="flex items-center">
            <span style={{ fontSize: 10 }} className="mr-3 font-medium">
              Show completed
            </span>
            <div
              className="flex items-center cursor-pointer w-10 h-4 relative rounded-full p-1"
              onClick={() => setShowCompleted(!showCompleted)}
            >
              <motion.div
                initial={false}
                animate={{
                  backgroundColor: showCompleted ? "#C3FB21" : "#CBD5E1",
                }}
                className="w-full h-full flex items-center absolute top-0 left-0 rounded-full -z-10"
              >
                <motion.div
                  className="bg-white w-3 h-3 rounded-full shadow-md"
                  layout
                  transition={spring}
                  animate={{
                    x: showCompleted ? 16 : 4,
                  }}
                />
              </motion.div>
            </div>
          </div>
        )}
        <div
          className="grid grid-cols-2 p-1 bg-slate-100 rounded-lg "
          style={{ minWidth: 240 }}
        >
          {tabs.map((tab) => (
            <p
              key={tab.name}
              onClick={() => setSelectedTab(tab.name)}
              style={{ fontSize: 11 }}
              className={`${
                tab.name === selectedTab
                  ? "font-semibold  shadow-sm bg-white text-gray-700"
                  : `text-gray-500 hover:border-gray-300 hover:text-gray-700`
              } rounded-md cursor-pointer text-center py-1`}
              aria-current={tab.name === selectedTab ? "page" : undefined}
            >
              {tab.name}
            </p>
          ))}
        </div>

        <div></div>
      </div>
      <div
        className="h-full w-full overflow-auto flex relative"
        ref={parentRef}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full w-full">
            <div className="flex items-center pt-20 justify-center h-full w-full">
              <ColorRing
                visible={true}
                height="24"
                width="24"
                ariaLabel="blocks-loading"
                colors={["#64748b", "#64748b", "#64748b", "#64748b", "#64748b"]}
              />
            </div>
          </div>
        ) : sortedAutomations?.length == 0 ? (
          <AllDone />
        ) : (
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              minWidth: 800,
              position: "relative",
            }}
            className="w-full max-w-3/4 border-r"
          >
            <AnimatePresence>
              {rowVirtualizer.getVirtualItems().map((virtualItem, index) => (
                <motion.div
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={rowVirtualizer.measureElement}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.1,
                    delay: index * 0.04,
                    ease: "easeInOut",
                  }}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <div className="">
                    {selectedTab == "Review" ? (
                      <ApprovalItem
                        tab={selectedTab}
                        toast={toast}
                        automation={sortedAutomations[virtualItem.index]}
                        onDelete={handleDelete}
                      />
                    ) : (
                      <ApprovalItem
                        tab={selectedTab}
                        toast={toast}
                        automation={sortedAutomations[virtualItem.index]}
                        onDelete={handleDelete}
                      />
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
        {true == false && (
          <div
            className="w-full px-4 flex items-center  justify-center h-full sticky top-0"
            style={{ maxWidth: 440 }}
          >
            <div className="text-center">
              <Dropzone
                onDrop={(acceptedFiles) => {
                  acceptedFiles.forEach((file) => {
                    buildAutomation(file);
                  });
                }}
                multiple={true}
              >
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <div
                    {...getRootProps()}
                    className={`cursor-pointer 
                   py-8 px-8 w-full rounded-xl`}
                  >
                    <input {...getInputProps()} />
                    <motion.div
                      animate={
                        isDragActive
                          ? {
                              rotate: [0, -5, 5, -5, 5, 0],
                              transition: {
                                duration: 0.5,
                                repeat: Infinity,
                                repeatType: "reverse",
                              },
                            }
                          : {}
                      }
                    >
                      <img
                        src={"./illustrations/scanDoc.png"}
                        className="mx-auto"
                        style={{ width: 80 }}
                      />
                    </motion.div>
                    <p className="font-semibold text-sm mb-2">
                      {isDragActive
                        ? "Drop files"
                        : "New automation from documents"}
                    </p>

                    {isDragActive ? (
                      <p
                        className="text-xs text-gray-600 mb-4 w-4/5 mx-auto"
                        style={{ minWidth: 260 }}
                      >
                        {" "}
                        Drop files here...{" "}
                      </p>
                    ) : (
                      <p
                        className="text-xs text-gray-600 mb-4 w-1/2 mx-auto"
                        style={{ minWidth: 200 }}
                      >
                        Drop files here to create new delivery orders from PDFs
                        and documents. Processed documents will be added to your
                        automation list for review.{" "}
                      </p>
                    )}
                    {!isDragActive && (
                      <button className="bg-slate-100 mx-auto rounded-lg text-xs text-slate-700 flex items-center justify-center space-x-2 font-medium py-2 px-4 hover:bg-slate-200 transition-colors">
                        <FolderUp size={16} strokeWidth={2.25} />
                        <p>Upload documents</p>
                      </button>
                    )}
                    {droppedFiles.length > 0 && (
                      <div className="mt-4 h-6 overflow-hidden">
                        <AnimatePresence mode="wait">
                          <motion.p
                            key={currentFileIndex}
                            variants={fileVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ duration: 0.5 }}
                            className="text-xs text-left"
                          >
                            {isUploading
                              ? "Uploading..."
                              : `${droppedFiles[currentFileIndex]} uploaded`}
                          </motion.p>
                        </AnimatePresence>
                      </div>
                    )}
                  </div>
                )}
              </Dropzone>
            </div>
          </div>
        )}
      </div>

      <Toaster
        position="bottom-center"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          // Define default options
          className: "",
          duration: 5000,
          style: {
            background: "#363636",
            fontSize: 14,
            color: "#fff",
            maxWidth: "500px",
          },

          // Default options for specific types
          success: {
            duration: 3000,
            theme: {
              primary: "green",
              secondary: "black",
            },
          },
        }}
      />
    </div>
  );
}
